import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  ProjectData,
  ProjectsList,
  ProjectMetrics,
  ProjectStructureResponse,
  ProjectStructure,
  ResourcesOfEntityResponse,
  ResourcesOfEntity,
} from '../shared/models/projects.model';
// import { baseURL } from './config';
import { UserService } from './user.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private projectsListURL = '/dashboards/project_list';
  private projectsMetricsURL = '/dashboards/project_metrics/';

  constructor(private http: HttpClient,
              private userService: UserService,
              private createMessage: NzMessageService) {}

  /**
   * Fetches the project metrics for a given project ID.
   *
   * @param {number} proyectID - The ID of the project to fetch metrics for.
   * @returns {Observable<ProjectMetrics>} An observable containing the project metrics.
   *
   * @throws {Error} If the response status is 'error', an error is thrown with the error message.
   *
   * @remarks
   * This method sends an HTTP GET request to fetch the project metrics. If the request fails,
   * an empty ProjectMetrics object is returned and the error is logged to the console.
   */
  public getProjectMetrics(
    proyectID: number | 'all'
  ): Observable<ProjectMetrics[]> {
    const headers = this.userService.headers;

    let URL = `${environment.baseURL}${this.projectsMetricsURL}${proyectID}`;

    return this.http.get<ProjectData>(URL, { headers }).pipe(
      map((response) => {
        if (response.status === 'error') {
          if ((response.error.error !== undefined) && ('type_id' in response.error.error)) {
            this.createMessage.warning(response.error?.error?.message ? response.error.error.message : '');
          } else {
            this.createMessage.warning('😕 Tuvimos un problema al cargar la información. Por favor, intenta nuevamente o contacta a nuestro equipo ACYST si el problema continúa.')
          }
        }
        if (Array.isArray(response.response)) {
          return response.response as ProjectMetrics[];
        } else {
          return [] as ProjectMetrics[];
        }
      }),
      catchError((error) => {
        this.createMessage.error('😕 Ocurrió un problema al cargar la información financiera del proyecto. Intenta nuevamente en unos momentos o contacta a nuestro equipo ACYST si el problema persiste.')
        console.error('No se pudo obtener los datos de los proyectos: ', error.message);
        return of({} as ProjectMetrics[]);
      })
    );
  }

  getLimitProjects(filter: string = "", filterMode: 'projects' | 'companies') {
    const page = 1;
    const limit = 20;

    let URL = `${environment.baseURL}${this.projectsListURL}?page=${page}&limit=${limit}`;

    if(filter && filterMode == 'projects') {
      URL = `${URL}&str_project=${filter}`;
    } else if (filter && filterMode == 'companies') {
      URL = `${URL}&str_company=${filter}`;
    }

    return this.http.get<ProjectData>(URL, { headers: this.userService.headers })
    .pipe(
      switchMap((response) => {
        if (response.status === 'error') {
          return of([] as ProjectsList[]);
        }
        if ('rows' in response.response) {
          return of(response.response.rows as ProjectsList[] ?? [] as ProjectsList[]);
          
        } else {
          return of([] as ProjectsList[]);
        }
      }),
    );

  }

  /**
   * Retrieves a list of projects based on the provided filters.
   *
   * @param {string} [str_project] - Optional filter for the project name.
   * @param {string} [str_company] - Optional filter for the company name.
   * @param {string} [str_city] - Optional filter for the city name.
   * @returns {Observable<ProjectsList[]>} An observable containing the list of projects.
   */
  public getProjectsList(
    str_project?: string,
    str_company?: string,
    str_city?: string
  ): Observable<ProjectsList[]> {
    const project_list: ProjectsList[] = [];

    let page = 1;
    let limit = 100;
    let URL = `${environment.baseURL}${this.projectsListURL}?page=${page}&limit=${limit}`;

    if (str_project) {
      URL = `${URL}&str_project=${str_project}`;
    }
    if (str_company) {
      URL = `${URL}&str_company=${str_company}`;
    }
    if (str_city) {
      URL = `${URL}&str_city=${str_city}`;
    }

    return this.fetchAllProjects(URL, project_list, page, limit);
  }

  /**
   * Fetches all projects from the given URL, handling pagination and errors.
   *
   * @param {string} URL - The URL to fetch projects from.
   * @param {HttpHeaders} headers - The HTTP headers to include in the request.
   * @param {ProjectsList[]} array - The array to store the fetched projects.
   * @param {number} page - The current page number.
   * @param {number} limit - The number of projects per page.
   * @returns {Observable<ProjectsList[]>} An observable that emits the list of projects.
   *
   * @throws {Error} If the response status is 'error' or the response type is invalid.
   */
  private fetchAllProjects(
    URL: string,
    array: ProjectsList[],
    page: number,
    limit: number
  ): Observable<ProjectsList[]> {
    const headers = this.userService.headers;

    return this.http.get<ProjectData>(URL, { headers }).pipe(
      switchMap((response) => {
        if (response.status === 'error') {
          throw new Error(response.error['has-error'].toString());
        }
        if ('rows' in response.response) {
          array.push(...(response.response.rows as ProjectsList[]));
          if (
            response.response.rows &&
            response.response.rows.length === limit
          ) {
            page++;
            const newURL = URL.replace(`page=${page - 1}`, `page=${page}`);
            return this.fetchAllProjects(newURL, array, page, limit);
          } else {
            return of(array);
          }
        } else {
          throw new Error('Invalid response type');
        }
      }),
      catchError((error) => {
        console.error('Error fetching projects list:', error);
        return of([]);
      })
    );
  }

  public getStructureOfAProject(
    projectID: number
  ): Observable<ProjectStructure[]> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/project/${projectID}/wbsList`;

    return this.http.get<ProjectStructureResponse>(URL, { headers }).pipe(
      map((response: ProjectStructureResponse) => {
        if (response.status === 'SUCCESS') {
          if ('ProjectComponents' in response.response) {
            return response.response.ProjectComponents as ProjectStructure[];
          } else {
            console.error('Error: response does not contain structure data');
            return [] as ProjectStructure[];
          }
        } else {
          console.error('Error fetching project structure:', response.error);
          return [] as ProjectStructure[];
        }
      }),
      catchError((error) => {
        console.error('Error fetching project structure:', error);
        return of({} as ProjectStructure[]);
      })
    );
  }

  public getResourcesOfEntity(
    entityID: number,
    context: string,
    lots: number[],
    resources: number[]
  ): Observable<ResourcesOfEntity> {
    const headers = this.userService.headers;
    let URL = `${environment.baseURL}/wbscomponent/${entityID}/wbsList?context=${context}`;
    if (lots.length > 0) {
      for (let i = 0; i < lots.length; i++) {
        URL += `&productionUnit=${lots[i]}`;
      }
    }
    if (resources.length > 0) {
      for (let i = 0; i < resources.length; i++) {
        URL += `&resourceId=${resources[i]}`;
      }
    }

    return this.http.get<ResourcesOfEntityResponse>(URL, { headers }).pipe(
      map((response: ResourcesOfEntityResponse) => {
        if (response.status === 'SUCCESS') {
          if ('Nodes' in response.response) {
            return response.response as ResourcesOfEntity;
          } else {
            console.error('Error: response does not contain resources data');
            return {} as ResourcesOfEntity;
          }
        } else {
          console.error('Error fetching resources of entity:', response.error);
          return {} as ResourcesOfEntity;
        }
      }),
      catchError((error) => {
        console.error('Error fetching resources of entity:', error);
        return of({} as ResourcesOfEntity);
      })
    );
  }

  public getLotsListOfEntity(
    entityID: number
  ): Observable<ResourcesOfEntity[] | boolean> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/wbscomponent/${entityID}/batchProduction`;

    return this.http.get<ResourcesOfEntityResponse>(URL, { headers }).pipe(
      map((response: ResourcesOfEntityResponse) => {
        if (response.status === 'SUCCESS') {
          if (
            'Nodes' in response.response &&
            response.response.id === entityID
          ) {
            return response.response.Nodes as ResourcesOfEntity[];
          } else {
            console.error('Error: response does not contain resources data');
            return false;
          }
        } else {
          console.error('Error fetching resources of entity:', response.error);
          return false;
        }
      }),
      catchError((error) => {
        console.error('Error fetching resources of entity:', error);
        return of(false);
      })
    );
  }
}
